'use client';

import type { ToolUIPart } from 'ai';
import type { ComponentProps } from 'react';
import { Check, LoaderCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Shimmer } from '../Shimmer';

type ToolActivityCopy = {
  active: string;
  done: string;
};

const TOOL_ACTIVITY_COPY: Readonly<Record<string, ToolActivityCopy>> = {
  // KnowledgeBaseSearchToolPlugin
  knowledge_base_search: {
    active: 'Searching clinical knowledge',
    done: 'Clinical knowledge retrieved',
  },
  calculator_tool: { active: 'Running calculation', done: 'Calculation complete' },
  faq_search_tool: { active: 'Searching knowledge base', done: 'Knowledge base searched' },
  web_search: { active: 'Searching the web', done: 'Web results retrieved' },
  google_places_tool: { active: 'Searching nearby places', done: 'Places found' },

  // PatientSearchToolsPlugin
  clinical_notes_search: { active: 'Reviewing clinical notes', done: 'Clinical notes reviewed' },
  conversation_history_search_tool: {
    active: 'Reviewing conversation history',
    done: 'History reviewed',
  },
  fetch_realtime_inventory: {
    active: 'Discovering available data streams',
    done: 'Data streams identified',
  },
  fetch_realtime_data: { active: 'Pulling latest patient data', done: 'Live data synced' },
  list_named_queries: { active: 'Identifying data sources', done: 'Data sources ready' },
  execute_named_query: { active: 'Querying patient records', done: 'Records retrieved' },

  // UrgentNotesToolsPlugin
  create_note_to_self: { active: 'Creating note', done: 'Note created' },
  update_note_to_self: { active: 'Updating note', done: 'Note updated' },
  search_notes_to_self: { active: 'Searching notes', done: 'Notes retrieved' },
  delete_note_to_self: { active: 'Removing note', done: 'Note removed' },

  // ClinicianToolsPlugin
  clinician_helper_tool: {
    active: 'Looking up clinician context',
    done: 'Clinician context ready',
  },
  search_medications: { active: 'Searching medications', done: 'Medications found' },
  search_pharmacies: { active: 'Searching pharmacies', done: 'Pharmacies found' },
  draft_prescription: { active: 'Drafting prescription', done: 'Prescription drafted' },
  search_lab_tests: { active: 'Searching lab tests', done: 'Lab tests found' },
  search_biomarkers: { active: 'Searching biomarkers', done: 'Biomarkers found' },
  draft_existing_lab: { active: 'Preparing lab order', done: 'Lab order prepared' },
  search_icd10_codes: { active: 'Looking up diagnosis codes', done: 'Diagnosis codes found' },
  send_follow_up: { active: 'Sending follow-up', done: 'Follow-up sent' },
  debug_invoke_librarian: { active: 'Consulting librarian', done: 'Librarian complete' },

  // Frontend UI interactions
  clickButton: { active: 'Performing action', done: 'Action complete' },
  updateCheckbox: { active: 'Updating selection', done: 'Selection updated' },
  updateTextField: { active: 'Updating Note', done: 'Note updated' },
  updateSelect: { active: 'Applying selection', done: 'Selection applied' },
};

function getToolActivityCopy(toolName: string): ToolActivityCopy {
  return TOOL_ACTIVITY_COPY[toolName] ?? { active: 'Working', done: 'Done' };
}

function isRunningState(state: ToolUIPart['state']): boolean {
  switch (state) {
    case 'input-streaming':
    case 'input-available':
    case 'approval-requested':
    case 'approval-responded':
      return true;
    case 'output-available':
    case 'output-error':
    case 'output-denied':
      return false;
    default:
      return false;
  }
}

export type ToolActivityLineProps = ComponentProps<'div'> & {
  toolName: string;
  state: ToolUIPart['state'];
};

export function ToolActivityLine({ toolName, state, className, ...props }: ToolActivityLineProps) {
  const copy = getToolActivityCopy(toolName);
  const running = isRunningState(state);

  const text =
    state === 'output-error'
      ? `${copy.active} failed`
      : state === 'output-denied'
        ? `${copy.active} denied`
        : running
          ? copy.active
          : copy.done;

  return (
    <div
      className={cn(
        'not-prose mb-2 flex items-center gap-1.5 text-xs text-muted-foreground',
        className
      )}
      {...props}
    >
      {running ? (
        <>
          <LoaderCircle className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
          <Shimmer as="p" className="text-xs font-medium">
            {text}
          </Shimmer>
        </>
      ) : (
        <>
          <Check className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">{text}</span>
        </>
      )}
    </div>
  );
}
